import { Button, Form } from 'antd';
import { memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from './context';
import { buildPaymentSummaryView, calculatePaymentMethodDiscount, moneyMask } from './helper';

type Props = {
  visible: boolean;
  loading: boolean;
  termsAccepted: boolean;
  onRequestTerms: () => void;
};

/**
 * Barra fixa de resumo + CTA (experimento, `?priceBar=sticky` dentro do showcase). Preço DINÂMICO
 * do método/parcela selecionado, via portal no body (fixed ancora na viewport). Trata avulso,
 * múltiplos pagamentos e recorrência via `buildPaymentSummaryView`.
 *
 * NUNCA se esconde por foco de campo: o botão de compra sumindo é risco direto de conversão.
 */
const StickyPriceBar = memo<Props>(({ visible, loading, termsAccepted, onRequestTerms }) => {
  const { form, getTotal, items, isRecurrence, firstItem, checkout } = useForm();
  const payments = Form.useWatch(['payments'], form) as any[] | undefined;
  const barRef = useRef<HTMLDivElement>(null);

  const view = buildPaymentSummaryView({
    payments,
    isRecurrence,
    firstItem,
    items,
    fallbackTotal: getTotal() / 100.0,
    freeInstallments: checkout?.freeInstallments,
  });

  // Economia do método/parcela selecionado (avulso), pra reforçar o benefício colado no CTA.
  const single = (payments ?? []).length === 1 ? payments![0] : undefined;
  const economyCents =
    !isRecurrence && single
      ? calculatePaymentMethodDiscount(
          single.paymentMethod,
          single.creditCard?.installments,
          items,
          checkout?.paymentMethodDiscounts
        )
      : 0;

  // Em avulso o valor vive no botão ("Comprar agora · R$ X") e a esquerda mostra o método. Em
  // assinatura o botão é "Assinar agora" (sem valor), então a esquerda leva o valor: linha
  // principal (adesão/assinatura + parcelas escolhidas) e uma secundária menor com o recorrente.
  const primaryText = isRecurrence
    ? [view.labelText, view.amountText].filter(Boolean).join(' · ')
    : view.detailText || view.labelText;

  // Linha secundária: na assinatura, o recorrente (cinza); em avulso com juros, o total a pagar
  // (bold discreto, já que o botão fica sem valor); senão, a economia do método (verde).
  const secondary = isRecurrence
    ? view.detailText
      ? { text: view.detailText, className: 'text-gray-500' }
      : null
    : view.interestTotalText
      ? { text: view.interestTotalText, className: 'font-semibold text-gray-800' }
      : economyCents > 0
        ? { text: `Você economiza R$ ${moneyMask.apply(economyCents / 100.0)}`, className: 'text-green-600' }
        : null;

  // Reserva espaço no fim do conteúdo só enquanto a barra está visível (some no topo, antes de
  // rolar) — evita o "buraco" de um espaçador fixo permanente.
  useEffect(() => {
    document.body.style.paddingBottom = visible ? `${barRef.current?.offsetHeight ?? 0}px` : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [visible, primaryText, secondary?.text, view.ctaText]);

  return createPortal(
    <div
      ref={barRef}
      className={`fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-[0_-2px_12px_rgba(0,0,0,0.1)] transition-transform duration-300 ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-sm text-gray-700">{primaryText}</span>
          {secondary && (
            <span className={`truncate text-xs ${secondary.className}`}>{secondary.text}</span>
          )}
        </div>

        <Button
          type="primary"
          size="large"
          disabled={loading}
          loading={loading}
          // Sem os termos aceitos, em vez de ficar mudo/desabilitado, leva o cliente até o aceite
          // (que vive no fluxo, longe da barra) e o destaca.
          onClick={() => (termsAccepted ? form.submit() : onRequestTerms())}
        >
          {view.ctaText}
        </Button>
      </div>
    </div>,
    document.body
  );
});

StickyPriceBar.displayName = 'StickyPriceBar';

export default StickyPriceBar;
